import DownloadButton from "../DownloadButton";
import ProfilePlaceholder from "../../assets/svgIcons/ProfilePlaceholder.svg";
import "../../styles/components/transactionPrintLayouts/HrEmployeeHistoryCardLayout.css";

const pickValue = (source, keys, fallback = "") => {
  if (!source) return fallback;

  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }

  return fallback;
};

const isMeaningful = (value) => {
  if (value === undefined || value === null) return false;
  const normalized = String(value).trim().toLowerCase();
  return !["", "-", "n/a", "na", "null", "undefined"].includes(normalized);
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const toTitleFromCode = (value = "") =>
  String(value)
    .split("_")
    .filter(Boolean)
    .map(
      (token) => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase(),
    )
    .join(" ");

const toSlug = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const sanitizeReportTitle = (value = "") => {
  const title = String(value || "").trim();
  if (!title) return "";
  const compact = title.replace(/\s+/g, "");
  if (compact.length <= 4 && !title.includes(" ")) {
    return "";
  }
  return title;
};

const resolveReportTitle = (headDetails = {}) => {
  const directTitle = sanitizeReportTitle(
    pickValue(headDetails, [
      "report_title",
      "form_title",
      "title",
      "transaction_name",
      "tran_name",
    ]),
  );

  if (directTitle) {
    return directTitle;
  }

  const codeTitle = sanitizeReportTitle(
    toTitleFromCode(
      pickValue(headDetails, ["transaction_code", "tran_code"]),
    ) || toTitleFromCode(pickValue(headDetails, ["transactionCode"])),
  );

  if (codeTitle) {
    return codeTitle;
  }

  return "EMPLOYEE HISTORY CARD";
};

const resolveRows = (itemDetails = [], headDetails = {}) => {
  const rows = (Array.isArray(itemDetails) ? itemDetails : [])
    .map((item, index) => ({
      sl_no: pickValue(item, ["sl_no", "slno", "serial_no"], index + 1),
      employee_code: pickValue(item, ["employee_code", "emp_code", "code"]),
      employee_name: pickValue(item, [
        "name_of_emp",
        "name_of_employee",
        "employee_name",
        "emp_name",
        "employee",
        "name",
      ]),
      department: pickValue(item, ["department", "dept", "name_of_department"]),
      designation: pickValue(item, ["designation", "role", "position"]),
      date_of_joining: formatDate(
        pickValue(item, [
          "date",
          "date_of_join",
          "date_of_joining",
          "joining_date",
          "doj",
        ]),
      ),
      qualification: pickValue(item, [
        "qualification",
        "education",
        "educational_qualification",
      ]),
      skill_category: pickValue(item, [
        "skill_category",
        "skill",
        "skill_type",
      ]),
      type_of_employment: pickValue(item, [
        "type_of_employment",
        "employment_type",
        "type",
      ]),
      training_attended: pickValue(item, [
        "training_attended",
        "training",
        "training_topic",
      ]),
      attendance_record: pickValue(item, ["attendance_record", "attendance"]),
      disciplinary_record: pickValue(item, [
        "disciplinary_record",
        "disciplinary",
      ]),
      performance_remarks: pickValue(item, [
        "performance_remarks",
        "remarks",
        "remark",
      ]),
      hr_verified_by: pickValue(item, ["hr_verified_by", "verified_by_hr"]),
      dept_head_verified_by: pickValue(item, [
        "dept_head_verified_by",
        "department_head_verified_by",
        "verified_by_dept_head",
      ]),
    }))
    .filter((row) =>
      Object.entries(row).some(
        ([key, value]) => key !== "sl_no" && isMeaningful(value),
      ),
    );

  if (rows.length > 0) {
    return rows;
  }

  const headRow = {
    sl_no: 1,
    employee_code: pickValue(headDetails, [
      "employee_code",
      "emp_code",
      "code",
    ]),
    employee_name: pickValue(headDetails, [
      "name_of_emp",
      "name_of_employee",
      "employee_name",
      "emp_name",
      "employee",
      "name",
    ]),
    department: pickValue(headDetails, [
      "department",
      "dept",
      "name_of_department",
    ]),
    designation: pickValue(headDetails, ["designation", "role", "position"]),
    date_of_joining: formatDate(
      pickValue(headDetails, [
        "date",
        "date_of_join",
        "date_of_joining",
        "joining_date",
        "doj",
      ]),
    ),
    qualification: pickValue(headDetails, [
      "qualification",
      "education",
      "educational_qualification",
    ]),
    skill_category: pickValue(headDetails, [
      "skill_category",
      "skill",
      "skill_type",
    ]),
    type_of_employment: pickValue(headDetails, [
      "type_of_employment",
      "employment_type",
      "type",
    ]),
    training_attended: pickValue(headDetails, [
      "training_attended",
      "training",
      "training_topic",
    ]),
    attendance_record: pickValue(headDetails, [
      "attendance_record",
      "attendance",
    ]),
    disciplinary_record: pickValue(headDetails, [
      "disciplinary_record",
      "disciplinary",
    ]),
    performance_remarks: pickValue(headDetails, [
      "performance_remarks",
      "remarks",
      "remark",
    ]),
    hr_verified_by: pickValue(headDetails, [
      "hr_verified_by",
      "verified_by_hr",
    ]),
    dept_head_verified_by: pickValue(headDetails, [
      "dept_head_verified_by",
      "department_head_verified_by",
      "verified_by_dept_head",
    ]),
  };

  return Object.entries(headRow).some(
    ([key, value]) => key !== "sl_no" && isMeaningful(value),
  )
    ? [headRow]
    : [];
};

export const HrEmployeeHistoryCardLayout = ({
  headDetails = {},
  companyData = {},
  itemDetails = [],
}) => {
  let organisationData = {};
  try {
    organisationData = JSON.parse(localStorage.getItem("organisation") || "{}");
  } catch (error) {
    organisationData = {};
  }

  const refNo =
    pickValue(headDetails, ["ref_no", "reference_no", "form_ref_no"]) ||
    "FP/F/TRG/08";
  const revNo = pickValue(headDetails, [
    "rev_no",
    "revision_no",
    "form_rev_no",
  ]);
  const revDate = formatDate(
    pickValue(headDetails, ["rev_date", "revision_date", "form_rev_date"]),
  );

  const preparedBy = pickValue(headDetails, ["prepared_by", "created_by"]);
  const approvedBy = pickValue(headDetails, [
    "approved_by",
    "approved_by_name",
  ]);
  const reportTitle = resolveReportTitle(headDetails);
  const reportFileName =
    pickValue(headDetails, [
      "download_file_name",
      "report_file_name",
      "file_name",
    ]) ||
    toSlug(reportTitle) ||
    "manual-report";

  const rows = resolveRows(itemDetails, headDetails);

  const columnWidths = [
    "6%",
    "9%",
    "7%",
    "7%",
    "7%",
    "8%",
    "6%",
    "8%",
    "9%",
    "7.5%",
    "6.5%",
    "8.5%",
    "5.5%",
    "5.5%",
  ];

  const tableData = {
    headings: [
      {
        display_name: "EMPLOYEE CODE",
        field_name: "employee_code",
        is_internal: false,
      },
      {
        display_name: "EMPLOYEE NAME",
        field_name: "employee_name",
        is_internal: false,
      },
      {
        display_name: "DEPARTMENT",
        field_name: "department",
        is_internal: false,
      },
      {
        display_name: "DESIGNATION",
        field_name: "designation",
        is_internal: false,
      },
      {
        display_name: "DATE OF JOINING",
        field_name: "date_of_joining",
        is_internal: false,
      },
      {
        display_name: "QUALIFICATION",
        field_name: "qualification",
        is_internal: false,
      },
      {
        display_name: "SKILL CATEGORY",
        field_name: "skill_category",
        is_internal: false,
      },
      {
        display_name: "TYPE OF EMPLOYMENT",
        field_name: "type_of_employment",
        is_internal: false,
      },
      {
        display_name: "TRAINING ATTENDED",
        field_name: "training_attended",
        is_internal: false,
      },
      {
        display_name: "ATTENDANCE RECORD",
        field_name: "attendance_record",
        is_internal: false,
      },
      {
        display_name: "DISCIPLINARY RECORD",
        field_name: "disciplinary_record",
        is_internal: false,
      },
      {
        display_name: "PERFORMANCE REMARKS",
        field_name: "performance_remarks",
        is_internal: false,
      },
      {
        display_name: "HR VERIFIED BY",
        field_name: "hr_verified_by",
        is_internal: false,
      },
      {
        display_name: "DEPT HEAD VERIFIED BY",
        field_name: "dept_head_verified_by",
        is_internal: false,
      },
    ],
    rows,
  };

  return (
    <div className="hr-employee-history-page">
      <div className="hr-employee-history-card">
        <div className="hr-employee-history-header-row">
          <div className="hr-employee-history-brand">
            <img
              src={organisationData?.logo_url || ProfilePlaceholder}
              alt="Company Logo"
              className="hr-employee-history-logo"
            />
            <div className="hr-employee-history-org-name">
              {organisationData?.name || companyData?.company_name || ""}
            </div>
          </div>
          <div className="hr-employee-history-title-box">{reportTitle}</div>
          <div className="hr-employee-history-ref-box">
            <div>Ref. No. : {refNo || "-"}</div>
            <div>Rev. No. : {revNo || "-"}</div>
            <div>Rev. Date : {revDate || "-"}</div>
          </div>
        </div>

        <table className="hr-employee-history-table">
          <colgroup>
            {columnWidths.map((width, index) => (
              <col key={`employee-history-col-${index}`} style={{ width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {tableData.headings.map((heading) => (
                <th key={heading.field_name}>{heading.display_name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={`employee-history-row-${row.sl_no}`}>
                <td>{row.employee_code}</td>
                <td>{row.employee_name}</td>
                <td>{row.department}</td>
                <td>{row.designation}</td>
                <td>{row.date_of_joining}</td>
                <td>{row.qualification}</td>
                <td>{row.skill_category}</td>
                <td>{row.type_of_employment}</td>
                <td>{row.training_attended}</td>
                <td>{row.attendance_record}</td>
                <td>{row.disciplinary_record}</td>
                <td>{row.performance_remarks}</td>
                <td>{row.hr_verified_by}</td>
                <td>{row.dept_head_verified_by}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={7}>Prepared By: {preparedBy}</td>
              <td colSpan={7}>Approved By: {approvedBy}</td>
            </tr>
          </tfoot>
        </table>

        <p className="invoice-system-generated-note">
          This is a system-generated document and does not require a signature.
        </p>

        <div className="invoice-buttons hr-employee-history-actions">
          <DownloadButton
            tableData={tableData}
            fileName={reportFileName}
            downloadFormats={["pdf", "full-page-pdf"]}
            fullPageDownload={true}
            fullPageSelector=".hr-employee-history-page"
          />
        </div>
      </div>
    </div>
  );
};
